import React, { useState, useEffect } from "react";
import { Modal, Button, Spinner } from "react-bootstrap";
import toast from "react-hot-toast";
import { useAuth } from "../../../context/LoginContext/AuthContext";

const PREDEFINED_REASONS = [
  "Too expensive / financial reasons",
  "My child is not using it enough",
  "Content is too difficult or not engaging enough",
  "Found an alternative platform / curriculum",
  "Temporary break / will subscribe again later",
  "Other (Please specify)",
];

export const SubscriptionCancelModal = ({
  show,
  onHide,
  subscriptionId,
  paymentMethod,
  kids = [],
  renewalDate = "",
  planName = "Subscription Plan",
  onSuccess,
}) => {
  const { fetchWithAuth } = useAuth();
  const apiUrl = process.env.REACT_APP_API_URL;

  const [selectedReason, setSelectedReason] = useState("");
  const [customReason, setCustomReason] = useState("");
  const [isCancelling, setIsCancelling] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  // Reset modal state on open/close
  useEffect(() => {
    if (show) {
      setSelectedReason("");
      setCustomReason("");
      setIsCancelling(false);
      setErrorMsg("");
    }
  }, [show]);

  const isOther = selectedReason === "Other (Please specify)";
  const isReasonProvided =
    Boolean(selectedReason) &&
    (!isOther || customReason.trim().length > 0);

  const handleConfirmCancel = async () => {
    if (!isReasonProvided) {
      setErrorMsg("Please select or enter a reason for cancellation.");
      return;
    }

    const finalReason = isOther ? customReason.trim() : selectedReason;
    const method = (paymentMethod || "stripe").toLowerCase().trim();

    setIsCancelling(true);
    setErrorMsg("");

    try {
      const response = await fetchWithAuth(
        `${apiUrl}/${method}/cancel-subscription/${subscriptionId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            reason: finalReason,
          }),
        }
      );

      const data = await response.json();

      if (response.ok) {
        toast.success(data.message || "Subscription cancelled successfully.");
        if (onSuccess) onSuccess();
        onHide();
      } else {
        const message = data.message || data.error || "Failed to cancel subscription.";
        toast.error(message);
        setErrorMsg(message);
      }
    } catch (error) {
      const message = error.message || "Network error. Please try again.";
      toast.error(message);
      setErrorMsg(message);
    } finally {
      setIsCancelling(false);
    }
  };

  const kidNames = Array.isArray(kids) ? kids.join(", ") : kids;

  return (
    <Modal show={show} onHide={isCancelling ? null : onHide} centered size="md">
      <Modal.Header closeButton={!isCancelling} className="border-0 pb-0">
        <Modal.Title className="fs-5 fw-bold text-dark">
          Cancel Subscription
        </Modal.Title>
      </Modal.Header>

      <Modal.Body className="pt-3">
        {/* Subscription Summary Box */}
        <div
          className="p-3 mb-3 rounded-3"
          style={{ backgroundColor: "#F8F9FA", border: "1px solid #E9ECEF" }}
        >
          <div className="d-flex justify-content-between align-items-center mb-2">
            <span className="fw-semibold text-secondary small">Plan:</span>
            <span className="fw-bold text-dark">{planName}</span>
          </div>
          {kidNames && (
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold text-secondary small">Kid(s) Covered:</span>
              <span className="fw-semibold text-dark text-end">{kidNames}</span>
            </div>
          )}
          {renewalDate && (
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold text-secondary small">Current Cycle Ends:</span>
              <span className="fw-semibold text-dark">{renewalDate}</span>
            </div>
          )}
        </div>

        {/* Multi-Kid Alert if applicable */}
        {Array.isArray(kids) && kids.length > 1 && (
          <div
            className="alert alert-warning d-flex align-items-start gap-2 py-2 px-3 small mb-3"
            role="alert"
            style={{ borderRadius: "8px", border: "1px solid #FFEBAA", backgroundColor: "#FFF8E6" }}
          >
            <i className="bi bi-people-fill flex-shrink-0 mt-1 text-warning-emphasis" />
            <div>
              <strong>Multi-Kid Subscription:</strong> This subscription covers <strong>{kids.length} kids</strong> ({kidNames}). Cancelling will end renewal for all {kids.length} children together. Individual kid cancellation is not available on shared plans.
            </div>
          </div>
        )}

        {/* Warning Alert */}
        <div
          className="alert alert-light border d-flex align-items-start gap-2 py-2 px-3 small mb-3"
          role="alert"
          style={{ borderRadius: "8px" }}
        >
          <i className="bi bi-info-circle-fill flex-shrink-0 mt-1 text-secondary" />
          <div>
            Automatic renewal for the next billing cycle will stop. Access will remain active until the end of the current period.
          </div>
        </div>

        {/* Compulsory Reason Selection */}
        <div className="mb-2">
          <label className="form-label fw-semibold text-dark small mb-2 d-flex justify-content-between">
            <span>
              Why are you cancelling? <span className="text-danger">*</span>
            </span>
            <span className="text-muted fw-normal small">(Required)</span>
          </label>

          <div className="d-flex flex-column gap-2">
            {PREDEFINED_REASONS.map((reason, idx) => (
              <label
                key={idx}
                className={`d-flex align-items-center gap-2 p-2 px-3 rounded-3 border ${
                  selectedReason === reason
                    ? "border-primary bg-primary-subtle text-primary fw-medium"
                    : "border-light-subtle text-dark"
                }`}
                style={{
                  cursor: isCancelling ? "not-allowed" : "pointer",
                  fontSize: "13.5px",
                  transition: "all 0.15s ease-in-out",
                }}
              >
                <input
                  type="radio"
                  name="cancellationReason"
                  value={reason}
                  checked={selectedReason === reason}
                  onChange={(e) => {
                    setSelectedReason(e.target.value);
                    setErrorMsg("");
                  }}
                  disabled={isCancelling}
                  style={{ cursor: "pointer" }}
                />
                <span>{reason}</span>
              </label>
            ))}
          </div>

          {/* Conditional Custom Reason Textarea */}
          {isOther && (
            <div className="mt-3">
              <label className="form-label text-secondary small fw-medium">
                Please specify your reason <span className="text-danger">*</span>:
              </label>
              <textarea
                className="form-control form-control-sm"
                rows="3"
                maxLength={255}
                placeholder="Tell us what we could do better (max 255 characters)..."
                value={customReason}
                onChange={(e) => {
                  setCustomReason(e.target.value);
                  setErrorMsg("");
                }}
                disabled={isCancelling}
                style={{ resize: "none", fontSize: "13.5px" }}
              />
              <div className="d-flex justify-content-between mt-1">
                <span className="text-danger small">
                  {customReason.trim().length === 0 && selectedReason && "Reason description is required."}
                </span>
                <span className="text-muted small ms-auto">
                  {customReason.length}/255
                </span>
              </div>
            </div>
          )}
        </div>

        {errorMsg && (
          <div className="alert alert-danger py-2 px-3 small mt-2 mb-0" role="alert">
            {errorMsg}
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="border-0 pt-0">
        <Button
          variant="outline-secondary"
          size="sm"
          onClick={onHide}
          disabled={isCancelling}
          className="px-3"
        >
          Keep Subscription
        </Button>
        <Button
          variant="danger"
          size="sm"
          onClick={handleConfirmCancel}
          disabled={!isReasonProvided || isCancelling}
          className="px-3 d-flex align-items-center gap-2"
        >
          {isCancelling ? (
            <>
              <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
              <span>Cancelling...</span>
            </>
          ) : (
            <span>Confirm Cancellation</span>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default SubscriptionCancelModal;
